import React, { useEffect, useState } from 'react';
import { SingleSubscription } from 'subscriptions/types/Subscription';
import { useTranslations } from '../../localization/useTranslations';
import SecurityModal from './CancelDialog';
import { formatDate } from '../../utils/formatDate';
import Button from '../../components/buttons/SimpleButton/Button';
import Icon from '../../common/components/Icon';
import StopRenewingDialog from './StopRenewingDialog';
import { stopRecurrentPayment } from '../services/SubscriptionService';

interface SimpleSubscriptionRowProps extends SingleSubscription {
  onRecurrentPaymentStop?: (paymentId: number) => void;
}

const SimpleSubscriptionRow = ({ subscription, onRecurrentPaymentStop = () => {} }: SimpleSubscriptionRowProps) => {
  const translations = useTranslations();

  // State variables
  const [openDropdowns, setOpenDropdowns] = useState<number[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isRenewDialogOpen, setRenewDialogOpen] = useState(false);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleButtonClick = () => {
    // Handle button click logic
    closeDropdowns();
    setDialogOpen(false);
  };

  const handleOpenRenewDialog = () => {
    setRenewDialogOpen(true);
  };

  const handleCloseRenewDialog = () => {
    setRenewDialogOpen(false);
  };

  const handleRenewButtonClick = async () => {
    // Handle button click logic
    if (subscription.recurrent_payment_id) {
      await stopRecurrentPayment(subscription.recurrent_payment_id);
    }

    onRecurrentPaymentStop(subscription.payment_id);
    closeDropdowns();
    setRenewDialogOpen(false);
  };

  /**
   * Toggles the visibility state of a dropdown based on its index.
   * @param {number} dropdownIndex - The index of the dropdown to toggle.
   * @param {React.MouseEvent<HTMLButtonElement>} event - The click event triggering the toggle.
   */
  const toggleDropdown = (dropdownIndex: number, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setOpenDropdowns((prevOpenDropdowns) => {
      if (prevOpenDropdowns.includes(dropdownIndex)) {
        return prevOpenDropdowns.filter((index) => index !== dropdownIndex);
      } else {
        return [dropdownIndex];
      }
    });
  };

  /**
   * Closes all open dropdowns.
   */
  const closeDropdowns = () => {
    setOpenDropdowns([]);
  };

  /**
   * Effect to handle clicks outside of dropdowns and close them.
   */
  useEffect(() => {
    /**
     * Handles outside click events to close open dropdowns.
     * @param {MouseEvent} event - The click event.
     */
    const handleOutsideClick = (event: MouseEvent) => {
      let clickedInsideDropdown = false;

      if (!clickedInsideDropdown) {
        closeDropdowns();
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [openDropdowns]);

  return (
    <tr id={`subscription-${subscription.id}`} className="my-subscriptions__table-row">
      <SecurityModal isOpen={isDialogOpen} onClose={handleCloseDialog} onButtonClick={handleButtonClick} />
      <StopRenewingDialog
        isOpen={isRenewDialogOpen}
        onClose={handleCloseRenewDialog}
        onButtonClick={handleRenewButtonClick}
      />
      <td className="my-subscriptions__table-data-wrapper">
        <span className="my-subscriptions__data-label-mobile basic basic-14">
          {translations?.myAccountSubscription?.subscriptionType || 'Subscription Type'}
        </span>
        <div className="my-subscriptions__data-inner-wrapper">
          <span className="my-subscriptions__table-data my-subscriptions__table-data--type basic basic-16">
            {subscription.name}
          </span>
          {!!subscription.is_recurrent && subscription.recurrence_status === 'active' && (
            <span
              style={{
                fontSize: '1.4rem',
                lineHeight: '2rem',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
              }}
            >
              {/* TODO: Translate */}
              Automatické obnovení {formatDate(subscription.recurrent_next_charge_at)}
            </span>
          )}
        </div>
      </td>
      <td className="my-subscriptions__table-data-wrapper">
        <span className="my-subscriptions__data-label-mobile basic basic-14">
          {translations?.myAccountSubscription?.validFrom || 'Valid from'}
        </span>
        <span className="my-subscriptions__table-data my-subscriptions__table-data--from basic basic-16">
          {formatDate(subscription.start_at)}
        </span>
      </td>
      <td className="my-subscriptions__table-data-wrapper">
        <span className="my-subscriptions__data-label-mobile basic basic-14">
          {translations?.myAccountSubscription?.validTo || 'Valid to'}
        </span>
        <span className="my-subscriptions__table-data my-subscriptions__table-data--to basic basic-16">
          {formatDate(subscription.end_at)}
        </span>
      </td>
      <td className="my-subscriptions__table-data-wrapper">
        <div className="my-subscriptions__table-data-button-container">
          {subscription.status === 'active' ? (
            <div className="my-subscriptions__table-data-button-inner-container">
              <button
                className="my-subscriptions__action-button button button--secondary button--medium"
                onClick={(e) => toggleDropdown(subscription.id, e)}
              >
                ...
              </button>
              <ul className={`my-subscriptions__dropdown ${openDropdowns.includes(subscription.id) ? 'open' : ''}`}>
                {subscription.url_renew && (
                  <li
                    data-dropdown-id={subscription.id}
                    className="my-subscriptions__dropdown-item my-subscriptions__dropdown-item--extend"
                  >
                    <a
                      href={subscription.url_renew}
                      className="my-subscriptions__dropdown-link my-subscriptions__dropdown-link--extend link"
                    >
                      {translations?.myAccountSubscription?.dropdownRenew || 'Renew'}
                    </a>
                  </li>
                )}
                <li className="my-subscriptions__dropdown-item" data-dropdown-id={subscription.id}>
                  <span
                    className="my-subscriptions__dropdown-link my-subscriptions__dropdown-link--cancel link"
                    onClick={handleOpenDialog}
                  >
                    {translations?.myAccountSubscription?.cancelSubscription || 'Cancel'}
                  </span>
                </li>
                {!!subscription.is_recurrent && subscription.recurrence_status === 'active' && (
                  <li className="my-subscriptions__dropdown-item" data-dropdown-id={subscription.id}>
                    <span
                      className="my-subscriptions__dropdown-link my-subscriptions__dropdown-link--cancel link"
                      onClick={handleOpenRenewDialog}
                    >
                      {/* TODO: Translate */}
                      Zrušení automatického obnovení
                    </span>
                  </li>
                )}
              </ul>
            </div>
          ) : (
            subscription.url_renew && (
              <a
                href={subscription.url_renew}
                className="my-subscriptions__action-button my-subscriptions__action-button--renew button button--secondary button--medium"
              >
                <Icon name="refresh" size="small" />

                {translations?.myAccountSubscription?.renewNow || 'Renew now'}
              </a>
            )
          )}
        </div>

        <div className="my-subscriptions__table-data-button-container my-subscriptions__table-data-button-container--mobile">
          {subscription.status === 'active' ? (
            <>
              {subscription.url_renew && (
                <div className="my-subscriptions__dropdown-item--extend">
                  <a
                    href={subscription.url_renew}
                    className="my-subscriptions__dropdown-link my-subscriptions__dropdown-link--extend button button--primary button--medium button--full-width"
                  >
                    <Icon name="refresh" size="medium" />

                    {translations?.myAccountSubscription?.dropdownRenew || 'Renew'}
                  </a>
                </div>
              )}

              <Button
                onClick={handleOpenDialog}
                className="my-subscriptions__dropdown-link my-subscriptions__dropdown-link--cancel"
                type="secondary"
                size="medium"
                label={translations?.myAccountSubscription?.cancelSubscription || 'Cancel'}
                fullWidth={true}
              ></Button>

              {subscription.is_recurrent && subscription.recurrence_status === 'active' && (
                <Button
                  onClick={handleOpenRenewDialog}
                  className="my-subscriptions__dropdown-link my-subscriptions__dropdown-link--cancel"
                  type="secondary"
                  size="medium"
                  label={'Zrušení automatického obnovení'}
                  fullWidth={true}
                ></Button>
              )}
            </>
          ) : (
            subscription.url_renew && (
              <a
                href={subscription.url_renew}
                className="my-subscriptions__action-button my-subscriptions__action-button--renew button button--secondary button--medium"
              >
                <Icon name="refresh" size="medium" />
                {translations?.myAccountSubscription?.renewNow || 'Renew now'}
              </a>
            )
          )}
        </div>
      </td>
    </tr>
  );
};

export default SimpleSubscriptionRow;
